import { useEffect, useState } from "react";
import { NavLink, useNavigate } from "react-router-dom";
import { toast } from "react-toastify";
import "../style/payment_page.css";
import "../style/order_History.css";
import { routePaths } from "../constants/routes";
import { saveCart } from "../services/cartService";
import { loadOrders } from "../services/orderService";

export default function OrderHistory() {
  const navigate = useNavigate();
  const [orders, setOrders] = useState([]);

  useEffect(() => {
    setOrders(loadOrders());
  }, []);

  const formatDate = (iso) =>
    new Date(iso).toLocaleDateString("en-IN", {
      year: "numeric",
      month: "short",
      day: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });

  const copyInvoice = async (invoiceNumber) => {
    try {
      await navigator.clipboard.writeText(invoiceNumber);
      toast.success(`Invoice ${invoiceNumber} copied.`);
    } catch {
      toast.info(`Invoice: ${invoiceNumber}`);
    }
  };

  const reorderItems = (order) => {
    saveCart(order.items);
    localStorage.setItem(
      "cartSummary",
      JSON.stringify({
        items: order.items.reduce((sum, item) => sum + Number(item.quantity || 0), 0),
        subtotal: order.subtotal,
        discount: order.discount,
        delivery: order.delivery,
        totalPayable: order.total,
      })
    );
    navigate("/add-to-cart");
  };

  return (
    <main className="history-container">
      <section className="history-hero">
        <div>
          <p className="checkout-eyebrow">Post-Purchase</p>
          <h1>Order History & Tracking</h1>
          <p>Review invoices, track shipments, and reorder previous purchases.</p>
        </div>
        <div className="history-stats">
          <strong>{orders.length}</strong>
          <span>Total orders</span>
        </div>
      </section>

      {orders.length === 0 && (
        <div className="empty-order-state">
          <h2>No orders yet</h2>
          <p>Complete a test checkout to unlock invoice, history, and tracking flows.</p>
          <NavLink to={routePaths.product} className="history-action-link primary-link">
            Browse Products
          </NavLink>
        </div>
      )}

      {orders.map((order) => {
        const statusClassName = `status-${order.status.replace(/\s+/g, "-")}`;

        return (
          <div className="order-card" key={order.id}>
            <div className="order-header">
              <h3>
                Order ID: <strong>#{order.invoiceNumber}</strong>
              </h3>
              <span className={`order-status ${statusClassName}`}>{order.status.toUpperCase()}</span>
            </div>

            <div className="order-details">
              <div className="order-overview-grid">
                <p>Date Placed: {formatDate(order.date)}</p>
                <p>Payment: {order.paymentMethod}</p>
                <p>Shipping To: {order.address.name}</p>
                <p>
                  Expected Delivery:{" "}
                  {new Date(order.estimatedDeliveryDate).toLocaleDateString("en-IN", {
                    day: "numeric",
                    month: "short",
                    year: "numeric",
                  })}
                </p>
              </div>

              <div className="order-item-preview-list">
                {order.items.map((item) => (
                  <div key={`${order.id}-${item.id}`} className="order-item-preview">
                    <div>
                      <strong>{item.name}</strong>
                      <span>{item.brand ? `${item.brand} · ${item.category}` : item.category}</span>
                    </div>
                    <div>
                      <strong>x{item.quantity}</strong>
                      <span>Rs.{(Number(item.price) * Number(item.quantity)).toLocaleString("en-IN")}</span>
                    </div>
                  </div>
                ))}
              </div>

              <div className="order-address-box">
                <strong>Delivery address</strong>
                <p>
                  {order.address.name}, {order.address.phone}
                  <br />
                  {order.address.address1}
                  {order.address.address2 ? `, ${order.address.address2}` : ""}
                  <br />
                  {order.address.city}, {order.address.state} - {order.address.pincode}
                </p>
              </div>

              <p className="order-total-line">
                Total Paid: <strong>Rs.{order.total.toLocaleString("en-IN")}</strong>
              </p>
              <div className="history-actions-row">
                <NavLink
                  to={routePaths.orderTracking(order.invoiceNumber)}
                  className="history-action-link secondary-link"
                >
                  Track Shipment
                </NavLink>
                <NavLink
                  to={routePaths.invoice(order.invoiceNumber)}
                  className="history-action-link primary-link"
                >
                  Open Invoice
                </NavLink>
                <button
                  type="button"
                  onClick={() => copyInvoice(order.invoiceNumber)}
                  className="history-inline-button"
                >
                  Copy Invoice No.
                </button>
                <button
                  type="button"
                  onClick={() => reorderItems(order)}
                  className="history-inline-button"
                >
                  Buy Again
                </button>
              </div>
            </div>
          </div>
        );
      })}
    </main>
  );
}
